import { Content } from 'antd/es/layout/layout';
import './ContentComponent.scss';
import { Analytics } from '@/pages/ReceptionistDashboard/Analytics';
import CallLogs from '../LogsComponent/CallLogs/CallLogs';
import TextLogs from '../LogsComponent/TextLogs/TextLogs';
import VoiceMailLogs from '../LogsComponent/VoiceMailLogs/VoiceMailLogs';
import CrmDashboard from '../CrmDashboard/CrmDashboard';
import VoiceLibrary from '../VoiceLibrary/voiceLibrary';
import { TextingPlatform } from '../TextingPlatForm/index';
import IntakeForms from '../IntakeForms/IntakeForms';
import { ReceptionistDashboardSiderItem } from 'src/enums/EReceptionistDashboardSiderItem';
import { PhoneNumberSettings } from '../TestItOut/v2/PhoneNumberSettings';
import ExploreFeaturesPage from '../ExploreFeatures';
import { Playground } from '../Playground';
import { Embeddables } from '../Embeddables';
import { PRStatsPage } from '@/pages/PRStats';
import ResellerPlans from '../ResellerPlans/ResellerPlans';
import { ManageNotifications } from '../Overview/ManageNotifications/ManageNotifications';
import TestReceptionist from '../TestReceptionist/TestReceptionist';
import { UserAccount } from '@/types/TAccounts';
import { useEffect } from 'react';
import AICustomizationStep from '../TestReceptionist/AICustomizationStep/AICustomizationStep';
import { FeatureFlag } from 'src/enums/EFeatureFlags';
import { useFeatureFlag } from 'src/Hooks/useFeatureFlag';
import { useLocation, useNavigate } from 'react-router-dom';
import ApiWorkflowTemplate from '../ApiWorkflowTemplate/ApiWorkflowTemplate';
import ManagePassword from '@/pages/UserDashboard/Setting/SettingMenu/ManagePassword/ManagePassword';
import ManageCredits from '@/pages/UserDashboard/Setting/SettingMenu/ManageCredits/ManageCredits';
import AccountSettings from '@/pages/UserDashboard/Setting/SettingMenu/AccountSettings/AccountSettings';
import ManageUsage from '@/pages/UserDashboard/Setting/SettingMenu/ManageSubscription/ManageUsage';
import ManageSubscription from '@/pages/UserDashboard/Setting/SettingMenu/ManageSubscription/ManageSubscription';
import Dashboard from '@/pages/UserDashboard/Dashboard/Dashboard';
import GeneralSettings from '../GeneralSettings/GeneralSettings';
import Inbox from '../LogsComponent/Inbox/inbox';
import { ContactSupport } from '../ContactSupport';
import { CalendarEvents } from '@/pages/Calendar/CalendarEvents';
import { SmartCRMDashboard } from '../SmartCRM/SmartCRMDashboard';
import TicketsPage from '../FormResponses/TicketsPage';
interface ContentComponentProps {
	currentPage: string;
	setOpened: (open: boolean) => void;
	setOpenedOnboarding: (open: boolean) => void;
	debrand: boolean;
	apiWorkflowRef: React.RefObject<HTMLDivElement>;
	gcalRef: React.RefObject<HTMLDivElement>;
	intakeRef: React.RefObject<HTMLDivElement>;
	currentStep: number;
	setCurrentStep: (step: any) => void;
	openDrawer?: () => void;
	isReceptionistLoading?: boolean;
	completedFeatures: number[];
	setCompletedFeatures?: any;
	loggedInUser: UserAccount;
}

const ContentComponent: React.FC<ContentComponentProps> = ({
	currentPage,
	debrand,
	setOpenedOnboarding,
}) => {
	// Add feature flag check
	const isGohighlevelIntegrationEnabled = useFeatureFlag(FeatureFlag.GOHIGHLEVEL_INTEGRATION);
	const calendarEventsFeatureEnabled = useFeatureFlag(FeatureFlag.CALENDAR_EVENTS);
	const isCrmV2Enabled = useFeatureFlag(FeatureFlag.CRM_V2);
	const isFormResponsesEnabled = useFeatureFlag(FeatureFlag.FORM_RESPONSES_PAGE);

	// Add location and navigate for redirection
	const navigate = useNavigate();
	const location = useLocation();

	// Check if we're on the integrations page without the feature flag enabled
	useEffect(() => {
		if (
			location.pathname === '/app-integrations' &&
			!isGohighlevelIntegrationEnabled &&
			currentPage === ReceptionistDashboardSiderItem.APPINTEGRATIONS
		) {
			// Redirect to home if integration feature is disabled
			navigate('/');
		}
	}, [location.pathname, isGohighlevelIntegrationEnabled, navigate, currentPage]);

	const inboundAgentContent = (currentPage: string) => {
		// Fall back to the switch based on currentPage
		switch (currentPage) {
			case ReceptionistDashboardSiderItem.SETUP_INSTRUCTIONS:
				return <PhoneNumberSettings setOpenedOnboarding={setOpenedOnboarding} debrand={true} />;
			case ReceptionistDashboardSiderItem.GET_STARTED:
				return <TestReceptionist />;
			case ReceptionistDashboardSiderItem.HIRE_AI_RECEPTIONIST:
				return <TestReceptionist />;
			case ReceptionistDashboardSiderItem.RECEPTIONIST:
				return <AICustomizationStep onTest={() => {}} showTestBtn={false} debrand={debrand} />;
			case ReceptionistDashboardSiderItem.NOTIFICATIONS:
				return <ManageNotifications />;
			case ReceptionistDashboardSiderItem.TEST_CALL:
				return <Playground debrand={debrand} />;
			case ReceptionistDashboardSiderItem.APPINTEGRATIONS:
				return isGohighlevelIntegrationEnabled ? <ApiWorkflowTemplate /> : null;
			case ReceptionistDashboardSiderItem.EMBEDDABLES:
				return <Embeddables />;
			case ReceptionistDashboardSiderItem.ANALYTICS:
				return <Analytics debrand={debrand} />;
			case ReceptionistDashboardSiderItem.PR_STATS:
				return <PRStatsPage />;
			case ReceptionistDashboardSiderItem.VOICE_LIBRARY:
				return <VoiceLibrary debrand={debrand} />;
			case ReceptionistDashboardSiderItem.TEXTING_AGENT:
				return <TextingPlatform debrand={debrand} isNoviUser={false} />;
			case ReceptionistDashboardSiderItem.INTAKE_FORMS:
				return <IntakeForms />;
			case ReceptionistDashboardSiderItem.CALL_LOGS:
				return <CallLogs debrand={debrand} isNoviUser={false} />;
			case ReceptionistDashboardSiderItem.ALL_LOGS:
				return <Inbox debrand={debrand} isNoviUser={false} />;
			case ReceptionistDashboardSiderItem.TEXT_LOGS:
				return <TextLogs />;
			case ReceptionistDashboardSiderItem.VOICE_MAIL_LOGS:
				return <VoiceMailLogs />;
			case ReceptionistDashboardSiderItem.CRM:
				return isCrmV2Enabled ? (
					<SmartCRMDashboard debrand={debrand} isNoviUser={false} />
				) : (
					<CrmDashboard isNoviUser={false} />
				);
			case ReceptionistDashboardSiderItem.EXPLORE_FEATURES:
				return <ExploreFeaturesPage />;
			case ReceptionistDashboardSiderItem.RESELLER_PLAN:
				return <ResellerPlans />;
			case ReceptionistDashboardSiderItem.PASSWORD:
				return <ManagePassword />;
			case ReceptionistDashboardSiderItem.CREDITS:
				return <ManageCredits />;
			case ReceptionistDashboardSiderItem.SETTINGS:
				return <AccountSettings isLoading={false} />;
			case ReceptionistDashboardSiderItem.USAGE:
				return <ManageUsage />;
			case ReceptionistDashboardSiderItem.SUBSCRIPTION:
				return <ManageSubscription />;
			case ReceptionistDashboardSiderItem.DASHBOARD:
				return <Dashboard />;
			case ReceptionistDashboardSiderItem.GENERAL_SETTINGS:
				return <GeneralSettings isLoading={false} />;
			case ReceptionistDashboardSiderItem.CONTACT_SUPPORT:
				return <ContactSupport />;
			case ReceptionistDashboardSiderItem.CALENDAR:
				return calendarEventsFeatureEnabled ? <CalendarEvents /> : null;
			case ReceptionistDashboardSiderItem.TICKETS:
				return isFormResponsesEnabled ? <TicketsPage /> : null;
			default:
				break;
		}
	};

	const renderContent = () => {
		return inboundAgentContent(currentPage);
	};

	const baseClass = 'receptionist-content-component';

	return (
		<Content className={baseClass}>
			<div className={`${baseClass}_content relative`}>{renderContent()}</div>
		</Content>
	);
};

export default ContentComponent;
